package one.devsbox.www.ui.widgets

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.FilterQuality
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.isActive
import kotlinx.coroutines.withContext
import one.devsbox.www.io.Repository
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import kotlin.math.ceil

@Composable
fun LazyImage(
    id: Long,
    modifierPlaceHolder: Modifier,
    modifierContent: Modifier,
    contentScale: ContentScale = ContentScale.Fit
) {
    val imageState = remember(id) {
        val cached = Repository.getCached(id)
        if (cached != null) {
            mutableDataState<ImageBitmap>().apply { value = DataState.Result(cached) }
        } else {
            mutableDataState<ImageBitmap>().apply { value = DataState.Loading() }
        }
    }
    Box(modifierPlaceHolder) {
        when (val state = imageState.value) {
            is DataState.Idle -> Unit
            is DataState.Error -> Text("Fetch ERR", Modifier.align(Alignment.Center))
            is DataState.Loading -> {
                CircularProgressIndicator(Modifier.align(Alignment.Center))
            }

            is DataState.Result -> {
                Image(
                    bitmap = state.nnvl,
                    alignment = Alignment.Center,
                    contentDescription = null,
                    contentScale = contentScale,
                    filterQuality = FilterQuality.High,
                    modifier = modifierContent
                )
            }
        }
    }
    LaunchedEffect(id) {
        if (imageState.value is DataState.Result) return@LaunchedEffect

        imageState.value = DataState.Loading()
        runCatching {
            Repository.fetchThumb(id)
        }.onSuccess {
            if (isActive) imageState.value = DataState.Result(it)
        }.onFailure {
            if (isActive) imageState.value = DataState.Error(it.message ?: "Fetch fail")
        }
    }
}


@Composable
fun LazyImage(
    id: Long,
    modifier: Modifier,
    contentScale: ContentScale = ContentScale.Fit,
    alphaBackground: Boolean = true
) {
    val imageState = remember(id) {
        val cached = Repository.getCached(id)
        if (cached != null) {
            mutableDataState<ImageBitmap>().apply { value = DataState.Result(cached) }
        } else {
            mutableDataState<ImageBitmap>().apply { value = DataState.Loading() }
        }
    }
    Box(modifier) {
        when (val state = imageState.value) {
            is DataState.Idle -> Unit
            is DataState.Error -> Text("Fetch ERR", Modifier.align(Alignment.Center))
            is DataState.Loading -> {
                CircularProgressIndicator(Modifier.align(Alignment.Center))
            }

            is DataState.Result -> {
                Image(
                    bitmap = state.nnvl,
                    alignment = Alignment.Center,
                    contentDescription = null,
                    contentScale = contentScale,
                    filterQuality = FilterQuality.High,
                    modifier = Modifier.fillMaxSize()
                        .then(
                            if (alphaBackground) Modifier.drawAlphaBackground()
                            else Modifier
                        )
                )
            }
        }
    }
    LaunchedEffect(id) {
        if (imageState.value is DataState.Result) return@LaunchedEffect

        imageState.value = DataState.Loading()
        runCatching {
            withContext(Dispatchers.Unconfined) {
                Repository.fetchThumb(id)
            }
        }.onSuccess {
            if (isActive) imageState.value = DataState.Result(it)
        }.onFailure {
            if (isActive) imageState.value = DataState.Error(it.message ?: "Fetch fail")
        }
    }
}

fun Modifier.drawAlphaBackground(): Modifier = drawBehind {
    val darkColor = Color(0xFF0F0F11)
    val lightColor = Color(0xFF151518)

    val cellPx = 16.dp.toPx()
    val cellCountX = (size.width / cellPx).runCatching { ceil(this).toInt() }.getOrNull() ?: return@drawBehind
    val cellCountY = (size.height / cellPx).runCatching { ceil(this).toInt() }.getOrNull() ?: return@drawBehind

    for (column in 0..cellCountX) {
        for (row in 0..cellCountY) {
            val color = if ((column + row) % 2 == 0) darkColor else lightColor

            val x = column * cellPx
            val y = row * cellPx

            val w = if (x + cellPx > size.width) size.width - x else cellPx
            val h = if (y + cellPx > size.height) size.height - y else cellPx

            if (w > 0 && h > 0) {
                drawRect(color, Offset(x, y), Size(w, h))
            }
        }
    }
}
